import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Megaphone, GraduationCap, School, X } from "lucide-react";

const AUDIENCE = {
  all:    { Icon: Megaphone,     tint: "from-[#fbd86b] to-[#f59849]", text: "text-amber-700",  badge: "Announcement" },
  school: { Icon: School,        tint: "from-[#1c8e8a] to-[#0d3b3f]", text: "text-teal-700",   badge: "School" },
  class:  { Icon: GraduationCap, tint: "from-[#4d80f4] to-[#2a4ea8]", text: "text-blue-700",   badge: "Class" },
  info:   { Icon: Bell,          tint: "from-[#d23028] to-[#a31f1a]", text: "text-red-700",    badge: "Info" },
};

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })
    + " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function NotificationPopup({ notification, onClose }) {
  useEffect(() => {
    if (!notification) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [notification, onClose]);

  if (!notification) return null;

  const cfg = AUDIENCE[(notification.audience || "info")] || AUDIENCE.info;
  const Icon = cfg.Icon;
  const img = typeof notification.image === "string" ? notification.image : notification.image?.url;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="absolute inset-0 bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        <motion.div
          className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 pt-6 pb-3 border-b border-[#e3eeee]">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${cfg.tint} grid place-items-center text-white shadow-sm`}>
                <Icon size={18} />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-[var(--cms-teal-deep)] leading-tight">{notification.title}</h3>
                <span className={`text-[10px] uppercase tracking-wider font-bold ${cfg.text}`}>{cfg.badge}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-zinc-100 transition shrink-0"
              aria-label="Close"
            >
              <X size={16} className="text-zinc-400" />
            </button>
          </div>

          <div className="px-6 py-4 space-y-4">
            {img && (
              <img src={img} alt="" loading="lazy" className="w-full rounded-xl object-cover max-h-48 border border-[#e3eeee]" />
            )}
            <p className="text-xs text-[var(--cms-muted)] font-medium flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--cms-teal)]" />
              {fmtDateTime(notification.created_at)}
            </p>
            {notification.body && (
              <div className="text-sm text-[var(--cms-teal-deep)] leading-relaxed whitespace-pre-wrap">
                {notification.body}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
